import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  LeaderboardSnapshot,
  LeaderboardSnapshotEntry,
} from '@shared/types';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { getLeaderboardSnapshot } from '../../services/economy';
import { ApiError } from '../../services/api';

const LIMIT_OPTIONS = [10, 50, 100, 500] as const;
type LimitOption = (typeof LIMIT_OPTIONS)[number];
const DEFAULT_LIMIT: LimitOption = 100;

function parseLimit(raw: string | null): LimitOption {
  if (!raw) return DEFAULT_LIMIT;
  const n = Number.parseInt(raw, 10);
  return (LIMIT_OPTIONS as readonly number[]).includes(n)
    ? (n as LimitOption)
    : DEFAULT_LIMIT;
}

function fmtDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * Admin leaderboard snapshot. Shows the current top-N players by wins. The
 * limit is user-selectable (10/50/100/500) and persisted to the URL. Rows
 * navigate to the user detail screen — the leaderboard is often the entry
 * point for moderation investigations ("who is at the top and why").
 *
 * Pagination is intentionally absent: the snapshot is capped at 500 rows
 * server-side, which is fine to render in a single scroll container.
 */
export default function LeaderboardSnapshotScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const limit = parseLimit(searchParams.get('limit'));

  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; data: LeaderboardSnapshot }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  const reqCounterRef = useRef(0);
  const fetchSnapshot = useCallback(async () => {
    const reqId = ++reqCounterRef.current;
    setState({ status: 'loading' });
    try {
      const data = await getLeaderboardSnapshot(limit);
      if (reqId !== reqCounterRef.current) return;
      setState({ status: 'ready', data });
    } catch (err) {
      if (reqId !== reqCounterRef.current) return;
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load leaderboard';
      setState({ status: 'error', message });
    }
  }, [limit]);

  useEffect(() => {
    void fetchSnapshot();
  }, [fetchSnapshot]);

  function handleLimitChange(next: LimitOption) {
    const params = new URLSearchParams(searchParams);
    if (next === DEFAULT_LIMIT) {
      params.delete('limit');
    } else {
      params.set('limit', String(next));
    }
    setSearchParams(params, { replace: true });
  }

  const rows =
    state.status === 'ready' ? state.data.entries : ([] as LeaderboardSnapshotEntry[]);

  const columns: DataTableColumn<LeaderboardSnapshotEntry>[] = useMemo(
    () => [
      {
        key: 'rank',
        header: 'Rank',
        render: (e) => (
          <span
            className="font-mono text-slate-900 font-semibold"
            data-testid={`leaderboard-rank-${e.rank}`}
          >
            #{e.rank}
          </span>
        ),
      },
      {
        key: 'displayName',
        header: 'Display name',
        render: (e) => (
          <span className="font-medium text-slate-900">{e.displayName}</span>
        ),
      },
      {
        key: 'wins',
        header: 'Wins',
        render: (e) => (
          <span className="font-mono text-slate-700">
            {e.wins.toLocaleString()}
          </span>
        ),
      },
      {
        key: 'coins',
        header: 'Coins',
        render: (e) => (
          <span className="font-mono text-amber-700">
            {e.coins.toLocaleString()}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4" data-testid="leaderboard-snapshot-screen">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-lg font-semibold text-slate-900">
          Top {limit} Leaderboard
        </h1>
        <div
          className="text-xs text-slate-500"
          data-testid="leaderboard-generated-at"
        >
          {state.status === 'ready'
            ? `Generated at: ${fmtDateTime(state.data.generatedAt)}`
            : state.status === 'loading'
              ? 'Loading…'
              : ''}
        </div>
      </div>

      <div className="bg-white rounded border border-slate-200 p-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs text-slate-600">
          Limit
          <select
            value={limit}
            onChange={(e) =>
              handleLimitChange(Number(e.target.value) as LimitOption)
            }
            data-testid="leaderboard-filter-limit"
            className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            {LIMIT_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                Top {opt}
              </option>
            ))}
          </select>
        </label>
      </div>

      {state.status === 'error' && (
        <div
          role="alert"
          data-testid="leaderboard-snapshot-error"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
        >
          {state.message}
        </div>
      )}

      <DataTable<LeaderboardSnapshotEntry>
        columns={columns}
        rows={rows}
        rowKey={(e) => e.userId}
        loading={state.status === 'loading'}
        onRowClick={(e) => navigate(`/users/${e.userId}`)}
        empty="No entries yet."
        testId="leaderboard-table"
      />
    </div>
  );
}
